import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createApiClient, apiClient, getLocalTimestamp } from "../../api/apiClient";
import { handleObservationErrorWithFriendlyMessage, OBSERVATION_API_NAMES } from "../utils/observationErrorHandler";

// Interface for Employee Performance data from API
export interface EmployeePerformanceData {
  GPID: string;
  Location_Name?: string | null;
  First_Name: string;
  Last_Name: string;
  Role: string;
  Manager?: string;
  Last_Observation_Date: string;
  Last_Pulse_Check_Date: string;
  Last_Observation_Date_Threshold: boolean;
  Last_Pulse_Check_Date_Threshold: boolean;
  TOUCH_CASES: string;
  TOTAL_CASES: string;
  GAP_TIME: string;
  WC: string;
  CPH: string;
  KU: string;
  DPS: string;
  fiscal_year: number;
  fiscal_week_in_year: number;
  fiscal_week_in_period: number;
}

// Interface for table display data
export interface EmployeeRowData {
  id: string;
  gpid: string;
  name: string;
  manager?: string;
  role: string;
  lastObservationDate: string | null;
  lastPulseCheckDate: string | null;
  lastObservationDateThreshold: boolean;
  lastPulseCheckDateThreshold: boolean;
  touchCases: string | null;
  totalCases: string | null;
  gapTime: string | null;
  wc: string;
  cph: string;
  ku: string;
  dps: string;
  fiscalYear: number | null;
  fiscalWeek: number | null;
  fiscalWeekInPeriod: number | null;
  rawData?: EmployeePerformanceData;
}

// Interface for API response
export interface EmployeePerformanceAPIResponse {
  status: string;
  message: string;
  data: EmployeePerformanceData[];
  count: number;
}

// Interface for Employee LCW state
export interface EmployeeLCWState {
  employeeData: EmployeeRowData[];
  fullEmployeeData: EmployeePerformanceData[];
  loading: boolean;
  error: string | null;
  showPeriodObservation: boolean;
  showWeeklyPulseCheck: boolean;
  filters: {
    showPeriodObservation: boolean;
    showWeeklyPulseCheck: boolean;
  };
}

// Initial state
const initialState: EmployeeLCWState = {
  employeeData: [],
  fullEmployeeData: [],
  loading: false,
  error: null,
  showPeriodObservation: false,
  showWeeklyPulseCheck: false,
  filters: {
    showPeriodObservation: false,
    showWeeklyPulseCheck: false,
  },
};

// Async thunk for fetching employee performance data
export const fetchEmployeePerformanceData = createAsyncThunk(
  "employeeLCW/fetchEmployeePerformanceData",
  async (
    payload: {
      locationName: string;
      pulseCheck?: boolean;
      periodObservation?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const api = createApiClient();

      // Build query parameters
      const params = new URLSearchParams({
        location_name: payload.locationName,
        pulseCheck: (payload.pulseCheck || false).toString(),
        periodObservation: (payload.periodObservation || false).toString(),
      });

      const endpoint = `/observations/employee-performance?${params.toString()}&clientTimestamp=${getLocalTimestamp()}`;

      const response: EmployeePerformanceAPIResponse = await apiClient(
        api,
        endpoint,
        "GET"
      );

      if (response.status === "success" && response.data) {
        return response.data;
      } else {
        throw new Error(
          response.message || "Failed to fetch employee performance data"
        );
      }
    } catch (error: any) {
      // Use centralized error handler with overlay
      handleObservationErrorWithFriendlyMessage(
        OBSERVATION_API_NAMES.FETCH_EMPLOYEE_PERFORMANCE,
        error,
        true, // Show error modal overlay
        {
          locationName: payload.locationName,
          pulseCheck: payload.pulseCheck,
          periodObservation: payload.periodObservation,
        }
      );
      
      return rejectWithValue(
        error.message || "Failed to fetch employee performance data"
      );
    }
  }
);

// Helper function to transform API data to table row data
const transformEmployeeData = (
  apiData: EmployeePerformanceData[]
): EmployeeRowData[] => {
  return apiData.map((item) => ({
    id: item.GPID?.trim() || "N/A",
    gpid: item.GPID?.trim() || "N/A",
    name: `${item.First_Name || ""} ${item.Last_Name || ""}`.trim() || "N/A",
    manager: item.Manager || "N/A",
    role: item.Role || "N/A",
    lastObservationDate: item.Last_Observation_Date || null,
    lastPulseCheckDate: item.Last_Pulse_Check_Date || null,
    lastObservationDateThreshold: item.Last_Observation_Date_Threshold || false,
    lastPulseCheckDateThreshold: item.Last_Pulse_Check_Date_Threshold || false,
    touchCases: item.TOUCH_CASES != undefined ? item.TOUCH_CASES : "N/A",
    totalCases: item.TOTAL_CASES != undefined ? item.TOTAL_CASES : "N/A",
    gapTime: item.GAP_TIME != undefined ? item.GAP_TIME : "N/A",
    wc: item.WC != undefined ? item.WC : "N/A",
    cph: item.CPH != undefined ? item.CPH : "N/A",
    ku: item.KU != undefined ? item.KU : "N/A",
    dps: item.DPS != undefined ? item.DPS : "N/A",
    fiscalYear: item.fiscal_year ?? null,
    fiscalWeek: item.fiscal_week_in_year ?? null,
    fiscalWeekInPeriod: item.fiscal_week_in_period ?? null,
    rawData: item,
  }));
};

// Create slice
const employeeLCWSlice = createSlice({
  name: "employeeLCW",
  initialState,
  reducers: {
    setShowPeriodObservation: (state, action: PayloadAction<boolean>) => {
      state.showPeriodObservation = action.payload;
      state.filters.showPeriodObservation = action.payload;
    },
    setShowWeeklyPulseCheck: (state, action: PayloadAction<boolean>) => {
      state.showWeeklyPulseCheck = action.payload;
      state.filters.showWeeklyPulseCheck = action.payload;
    },
    setEmployeeData: (state, action: PayloadAction<EmployeeRowData[]>) => {
      state.employeeData = action.payload;
    },
    setFullEmployeeData: (
      state,
      action: PayloadAction<EmployeePerformanceData[]>
    ) => {
      state.fullEmployeeData = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetFilters: (state) => {
      state.filters = {
        showPeriodObservation: false,
        showWeeklyPulseCheck: false,
      };
      state.showPeriodObservation = false;
      state.showWeeklyPulseCheck = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEmployeePerformanceData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeePerformanceData.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.fullEmployeeData = action.payload;
        state.employeeData = transformEmployeeData(action.payload);
      })
      .addCase(fetchEmployeePerformanceData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.employeeData = [];
        state.fullEmployeeData = [];
      });
  },
});

// Export actions
export const {
  setShowPeriodObservation,
  setShowWeeklyPulseCheck,
  setEmployeeData,
  setFullEmployeeData,
  setLoading,
  setError,
  clearError,
  resetFilters,
} = employeeLCWSlice.actions;

// Export reducer
export default employeeLCWSlice.reducer;
